/**
 * @file permissionsConfig.js
 * @description Defines the available operations for permissions.
 */

/**
 * @constant {Object} Operations
 * @description Defines the available operations for permissions.
 * @property {string} READ - Read operation.
 * @property {string} CREATE - Create operation.
 * @property {string} UPDATE - Update operation.
 * @property {string} DELETE - Delete operation.
 */
export const Operations = {
  READ: "READ",
  CREATE: "CREATE",
  UPDATE: "UPDATE",
  DELETE: "Delete",
}

/**
 * @constant {Object} Roles
 * @description Defines the available roles for permissions.
 */
export const Roles = {
  ADM: "ADM",
  REGIONAL_LEAD: "REGIONAL_LEAD",
  ADMIN: "ADMIN",
  ADM_GVL: "ADM_GVL",
  IDM_USER: "IDM_USER",
  IDM_DISPONENT: "IDM_DISPONENT",
  IDM_LEAD: "IDM_LEAD",
  IDM_THEKE: "IDM_THEKE",
  IDM_SPRINGER: "IDM_SPRINGER",
  IDM_SPRINGER_LEAD: "IDM_SPRINGER_LEAD",
  IDM_PROCUREMENT: "IDM_PROCUREMENT",
}

/**
 * @constant {Object} DomainObjects
 * @description Defines the available domain objects for permissions.
 */
export const DomainObjects = {
  ADDRESSES: "ADDRESSES",
  AHG: "AHG",
  ARTICLES: "ARTICLES",
  ARTICLE_GROUPS: "ARTICLE_GROUPS",
  BONI: "BONI",
  BRANDS: "BRANDS",
  BRANCHES: "BRANCHES",
  CUSTOMERS: "CUSTOMERS",
  MAILING_LIST: "MAILING_LIST",
  MIXED_PRODUCTS: "MIXED_PRODUCTS",
  NOTES: "NOTES",
  OBLIGATIONS: "OBLIGATIONS",
  ORDERS: "ORDERS",
  PRICES: "PRICES",
  PRODUCTS: "PRODUCTS",
  PRODUCT_STOCK: "PRODUCT_STOCK",
  RESERVATION: "RESERVATION",
  REVENUES: "REVENUES",
  SPECIAL_OFFERS: "SPECIAL_OFFERS",
  CARTS: "CARTS",
  TOURS: "TOURS",
  VENDORS: "VENDORS",
  VISIT_REPORTS: "VISIT_REPORTS",
  VOLUME: "VOLUME",
  COMPLAINTS: "COMPLAINTS",
  PICKLISTS: "PICKLISTS",
  DOWNLOAD_CENTER: "DOWNLOAD_CENTER",
}

/**
 * @constant {Object} RolePermissions
 * @description Defines the role-based permissions for domain objects and operations.
 */
export const RolePermissions = {
  [DomainObjects.ADDRESSES]: {
    [Operations.READ]: {
      [Roles.ADM]: true,
      [Roles.REGIONAL_LEAD]: true,
      [Roles.IDM_USER]: true,
      [Roles.IDM_DISPONENT]: true,
      [Roles.IDM_LEAD]: true,
      [Roles.IDM_THEKE]: true,
      [Roles.ADM_GVL]: true,
      [Roles.IDM_SPRINGER]: true,
      [Roles.IDM_SPRINGER_LEAD]: true,
      [Roles.IDM_PROCUREMENT]: true,
    },
    [Operations.CREATE]: {
      [Roles.ADM]: true,
      [Roles.REGIONAL_LEAD]: true,
      [Roles.IDM_USER]: true,
      [Roles.IDM_DISPONENT]: true,
      [Roles.IDM_LEAD]: true,
      [Roles.IDM_THEKE]: true,
      [Roles.ADM_GVL]: true,
      [Roles.IDM_SPRINGER]: true,
      [Roles.IDM_SPRINGER_LEAD]: true,
      [Roles.IDM_PROCUREMENT]: true,
    },
    [Operations.UPDATE]: {
      [Roles.ADM]: true,
      [Roles.REGIONAL_LEAD]: true,
      [Roles.IDM_USER]: true,
      [Roles.IDM_DISPONENT]: true,
      [Roles.IDM_LEAD]: true,
      [Roles.IDM_THEKE]: true,
      [Roles.ADM_GVL]: true,
      [Roles.IDM_SPRINGER]: true,
      [Roles.IDM_SPRINGER_LEAD]: true,
      [Roles.IDM_PROCUREMENT]: true,
    },
    [Operations.DELETE]: {
      [Roles.ADM]: true,
      [Roles.REGIONAL_LEAD]: true,
      [Roles.IDM_USER]: true,
      [Roles.IDM_DISPONENT]: true,
      [Roles.IDM_LEAD]: true,
      [Roles.IDM_THEKE]: true,
      [Roles.ADM_GVL]: true,
      [Roles.IDM_SPRINGER]: true,
      [Roles.IDM_SPRINGER_LEAD]: true,
      [Roles.IDM_PROCUREMENT]: true,
    },
  },
  [DomainObjects.AHG]: {
    [Operations.READ]: {
      [Roles.ADM]: true,
      [Roles.REGIONAL_LEAD]: true,
      [Roles.IDM_USER]: true,
      [Roles.IDM_DISPONENT]: true,
      [Roles.IDM_LEAD]: true,
      [Roles.IDM_THEKE]: true,
      [Roles.ADM_GVL]: true,
      [Roles.IDM_SPRINGER]: true,
      [Roles.IDM_SPRINGER_LEAD]: true,
      [Roles.IDM_PROCUREMENT]: true,
    },
  },
  [DomainObjects.ARTICLE_GROUPS]: {
    [Operations.READ]: {
      [Roles.ADM]: true,
      [Roles.REGIONAL_LEAD]: true,
      [Roles.IDM_USER]: true,
      [Roles.IDM_DISPONENT]: true,
      [Roles.IDM_LEAD]: true,
      [Roles.IDM_THEKE]: true,
      [Roles.ADM_GVL]: true,
      [Roles.IDM_SPRINGER]: true,
      [Roles.IDM_SPRINGER_LEAD]: true,
      [Roles.IDM_PROCUREMENT]: true,
    },
  },
  [DomainObjects.ARTICLES]: {
    [Operations.READ]: {
      [Roles.ADM]: true,
      [Roles.REGIONAL_LEAD]: true,
      [Roles.IDM_USER]: true,
      [Roles.IDM_DISPONENT]: true,
      [Roles.IDM_LEAD]: true,
      [Roles.IDM_THEKE]: true,
      [Roles.ADM_GVL]: true,
      [Roles.IDM_SPRINGER]: true,
      [Roles.IDM_SPRINGER_LEAD]: true,
      [Roles.IDM_PROCUREMENT]: true,
    },
  },
  [DomainObjects.BONI]: {
    [Operations.READ]: {
      [Roles.ADM]: true,
      [Roles.REGIONAL_LEAD]: true,
      [Roles.IDM_USER]: true,
      [Roles.IDM_DISPONENT]: true,
      [Roles.IDM_LEAD]: true,
      [Roles.IDM_THEKE]: true,
      [Roles.ADM_GVL]: true,
      [Roles.IDM_SPRINGER]: true,
      [Roles.IDM_SPRINGER_LEAD]: true,
      [Roles.IDM_PROCUREMENT]: true,
    },
  },
  [DomainObjects.BRANDS]: {
    [Operations.READ]: {
      [Roles.ADM]: true,
      [Roles.REGIONAL_LEAD]: true,
      [Roles.IDM_USER]: true,
      [Roles.IDM_DISPONENT]: true,
      [Roles.IDM_LEAD]: true,
      [Roles.IDM_THEKE]: true,
      [Roles.ADM_GVL]: true,
      [Roles.IDM_SPRINGER]: true,
      [Roles.IDM_SPRINGER_LEAD]: true,
      [Roles.IDM_PROCUREMENT]: true,
    },
  },
  [DomainObjects.BRANCHES]: {
    [Operations.READ]: {
      [Roles.ADM]: true,
      [Roles.REGIONAL_LEAD]: true,
      [Roles.IDM_USER]: true,
      [Roles.IDM_DISPONENT]: true,
      [Roles.IDM_LEAD]: true,
      [Roles.IDM_THEKE]: true,
      [Roles.ADM_GVL]: true,
      [Roles.IDM_SPRINGER]: true,
      [Roles.IDM_SPRINGER_LEAD]: true,
      [Roles.IDM_PROCUREMENT]: true,
    },
  },
  [DomainObjects.CARTS]: {
    [Operations.READ]: {
      [Roles.ADM]: true,
      [Roles.REGIONAL_LEAD]: true,
      [Roles.IDM_USER]: true,
      [Roles.IDM_DISPONENT]: true,
      [Roles.IDM_LEAD]: true,
      [Roles.IDM_THEKE]: true,
      [Roles.ADM_GVL]: true,
      [Roles.IDM_SPRINGER]: true,
      [Roles.IDM_SPRINGER_LEAD]: true,
      [Roles.IDM_PROCUREMENT]: true,
    },
    [Operations.CREATE]: {
      [Roles.ADM]: true,
      [Roles.REGIONAL_LEAD]: true,
      [Roles.IDM_USER]: true,
      [Roles.IDM_DISPONENT]: true,
      [Roles.IDM_LEAD]: true,
      [Roles.IDM_THEKE]: true,
      [Roles.ADM_GVL]: true,
      [Roles.IDM_SPRINGER]: true,
      [Roles.IDM_SPRINGER_LEAD]: true,
      [Roles.IDM_PROCUREMENT]: true,
    },
    [Operations.UPDATE]: {
      [Roles.ADM]: true,
      [Roles.REGIONAL_LEAD]: true,
      [Roles.IDM_USER]: true,
      [Roles.IDM_DISPONENT]: true,
      [Roles.IDM_LEAD]: true,
      [Roles.IDM_THEKE]: true,
      [Roles.ADM_GVL]: true,
      [Roles.IDM_SPRINGER]: true,
      [Roles.IDM_SPRINGER_LEAD]: true,
      [Roles.IDM_PROCUREMENT]: true,
    },
    [Operations.DELETE]: {
      [Roles.ADM]: true,
      [Roles.REGIONAL_LEAD]: true,
      [Roles.IDM_USER]: true,
      [Roles.IDM_DISPONENT]: true,
      [Roles.IDM_LEAD]: true,
      [Roles.IDM_THEKE]: true,
      [Roles.ADM_GVL]: true,
      [Roles.IDM_SPRINGER]: true,
      [Roles.IDM_SPRINGER_LEAD]: true,
      [Roles.IDM_PROCUREMENT]: true,
    },
  },
  [DomainObjects.CUSTOMERS]: {
    [Operations.READ]: {
      [Roles.ADM]: true,
      [Roles.REGIONAL_LEAD]: true,
      [Roles.IDM_USER]: true,
      [Roles.IDM_DISPONENT]: true,
      [Roles.IDM_LEAD]: true,
      [Roles.IDM_THEKE]: true,
      [Roles.ADM_GVL]: true,
      [Roles.IDM_SPRINGER]: true,
      [Roles.IDM_SPRINGER_LEAD]: true,
      [Roles.IDM_PROCUREMENT]: true,
    },
    [Operations.UPDATE]: {
      [Roles.ADM]: true,
      [Roles.REGIONAL_LEAD]: true,
      [Roles.IDM_USER]: true,
      [Roles.IDM_DISPONENT]: true,
      [Roles.IDM_LEAD]: true,
      [Roles.IDM_THEKE]: true,
      [Roles.ADM_GVL]: true,
      [Roles.IDM_SPRINGER]: true,
      [Roles.IDM_SPRINGER_LEAD]: true,
      [Roles.IDM_PROCUREMENT]: true,
    },
  },
  [DomainObjects.MAILING_LIST]: {
    [Operations.READ]: {
      [Roles.ADM]: true,
      [Roles.REGIONAL_LEAD]: true,
      [Roles.IDM_USER]: false,
      [Roles.IDM_DISPONENT]: false,
      [Roles.IDM_LEAD]: false,
      [Roles.IDM_THEKE]: false,
      [Roles.ADM_GVL]: true,
      [Roles.IDM_SPRINGER]: true,
      [Roles.IDM_SPRINGER_LEAD]: true,
      [Roles.IDM_PROCUREMENT]: false,
    },
  },
  [DomainObjects.MIXED_PRODUCTS]: {
    [Operations.READ]: {
      [Roles.ADM]: true,
      [Roles.REGIONAL_LEAD]: true,
      [Roles.IDM_USER]: true,
      [Roles.IDM_DISPONENT]: true,
      [Roles.IDM_LEAD]: true,
      [Roles.IDM_THEKE]: true,
      [Roles.ADM_GVL]: true,
      [Roles.IDM_SPRINGER]: true,
      [Roles.IDM_SPRINGER_LEAD]: true,
      [Roles.IDM_PROCUREMENT]: true,
    },
  },
  [DomainObjects.NOTES]: {
    [Operations.READ]: {
      [Roles.ADM]: true,
      [Roles.REGIONAL_LEAD]: true,
      [Roles.IDM_USER]: true,
      [Roles.IDM_DISPONENT]: true,
      [Roles.IDM_LEAD]: true,
      [Roles.IDM_THEKE]: true,
      [Roles.ADM_GVL]: true,
      [Roles.IDM_SPRINGER]: true,
      [Roles.IDM_SPRINGER_LEAD]: true,
      [Roles.IDM_PROCUREMENT]: true,
    },
    [Operations.CREATE]: {
      [Roles.ADM]: true,
      [Roles.REGIONAL_LEAD]: true,
      [Roles.IDM_USER]: true,
      [Roles.IDM_DISPONENT]: true,
      [Roles.IDM_LEAD]: true,
      [Roles.IDM_THEKE]: true,
      [Roles.ADM_GVL]: true,
      [Roles.IDM_SPRINGER]: true,
      [Roles.IDM_SPRINGER_LEAD]: true,
      [Roles.IDM_PROCUREMENT]: true,
    },
    [Operations.UPDATE]: {
      [Roles.ADM]: true,
      [Roles.REGIONAL_LEAD]: true,
      [Roles.IDM_USER]: true,
      [Roles.IDM_DISPONENT]: true,
      [Roles.IDM_LEAD]: true,
      [Roles.IDM_THEKE]: true,
      [Roles.ADM_GVL]: true,
      [Roles.IDM_SPRINGER]: true,
      [Roles.IDM_SPRINGER_LEAD]: true,
      [Roles.IDM_PROCUREMENT]: true,
    },
    [Operations.DELETE]: {
      [Roles.ADM]: true,
      [Roles.REGIONAL_LEAD]: true,
      [Roles.IDM_USER]: true,
      [Roles.IDM_DISPONENT]: true,
      [Roles.IDM_LEAD]: true,
      [Roles.IDM_THEKE]: true,
      [Roles.ADM_GVL]: true,
      [Roles.IDM_SPRINGER]: true,
      [Roles.IDM_SPRINGER_LEAD]: true,
      [Roles.IDM_PROCUREMENT]: true,
    },
  },
  [DomainObjects.OBLIGATIONS]: {
    [Operations.READ]: {
      [Roles.ADM]: true,
      [Roles.REGIONAL_LEAD]: true,
      [Roles.IDM_USER]: true,
      [Roles.IDM_DISPONENT]: true,
      [Roles.IDM_LEAD]: true,
      [Roles.IDM_THEKE]: true,
      [Roles.ADM_GVL]: true,
      [Roles.IDM_SPRINGER]: true,
      [Roles.IDM_SPRINGER_LEAD]: true,
      [Roles.IDM_PROCUREMENT]: true,
    },
  },
  [DomainObjects.ORDERS]: {
    [Operations.READ]: {
      [Roles.ADM]: true,
      [Roles.REGIONAL_LEAD]: true,
      [Roles.IDM_USER]: true,
      [Roles.IDM_DISPONENT]: true,
      [Roles.IDM_LEAD]: true,
      [Roles.IDM_THEKE]: true,
      [Roles.ADM_GVL]: true,
      [Roles.IDM_SPRINGER]: true,
      [Roles.IDM_SPRINGER_LEAD]: true,
      [Roles.IDM_PROCUREMENT]: true,
    },
    [Operations.CREATE]: {
      [Roles.ADM]: true,
      [Roles.REGIONAL_LEAD]: true,
      [Roles.IDM_USER]: true,
      [Roles.IDM_DISPONENT]: true,
      [Roles.IDM_LEAD]: true,
      [Roles.IDM_THEKE]: true,
      [Roles.ADM_GVL]: true,
      [Roles.IDM_SPRINGER]: true,
      [Roles.IDM_SPRINGER_LEAD]: true,
      [Roles.IDM_PROCUREMENT]: true,
    },
  },
  [DomainObjects.PRICES]: {
    [Operations.READ]: {
      [Roles.ADM]: true,
      [Roles.REGIONAL_LEAD]: true,
      [Roles.IDM_USER]: true,
      [Roles.IDM_DISPONENT]: true,
      [Roles.IDM_LEAD]: true,
      [Roles.IDM_THEKE]: true,
      [Roles.ADM_GVL]: true,
      [Roles.IDM_SPRINGER]: true,
      [Roles.IDM_SPRINGER_LEAD]: true,
      [Roles.IDM_PROCUREMENT]: true,
    },
  },
  [DomainObjects.PRODUCTS]: {
    [Operations.READ]: {
      [Roles.ADM]: true,
      [Roles.REGIONAL_LEAD]: true,
      [Roles.IDM_USER]: true,
      [Roles.IDM_DISPONENT]: true,
      [Roles.IDM_LEAD]: true,
      [Roles.IDM_THEKE]: true,
      [Roles.ADM_GVL]: true,
      [Roles.IDM_SPRINGER]: true,
      [Roles.IDM_SPRINGER_LEAD]: true,
      [Roles.IDM_PROCUREMENT]: true,
    },
  },
  [DomainObjects.PRODUCT_STOCK]: {
    [Operations.READ]: {
      [Roles.ADM]: true,
      [Roles.REGIONAL_LEAD]: true,
      [Roles.IDM_USER]: true,
      [Roles.IDM_DISPONENT]: true,
      [Roles.IDM_LEAD]: true,
      [Roles.IDM_THEKE]: true,
      [Roles.ADM_GVL]: true,
      [Roles.IDM_SPRINGER]: true,
      [Roles.IDM_SPRINGER_LEAD]: true,
      [Roles.IDM_PROCUREMENT]: true,
    },
  },
  [DomainObjects.RESERVATION]: {
    [Operations.READ]: {
      [Roles.ADM]: true,
      [Roles.REGIONAL_LEAD]: true,
      [Roles.IDM_USER]: true,
      [Roles.IDM_DISPONENT]: true,
      [Roles.IDM_LEAD]: true,
      [Roles.IDM_THEKE]: true,
      [Roles.ADM_GVL]: true,
      [Roles.IDM_SPRINGER]: true,
      [Roles.IDM_SPRINGER_LEAD]: true,
      [Roles.IDM_PROCUREMENT]: true,
    },
    [Operations.CREATE]: {
      [Roles.ADM]: false,
      [Roles.REGIONAL_LEAD]: false,
      [Roles.IDM_USER]: false,
      [Roles.IDM_DISPONENT]: false,
      [Roles.IDM_LEAD]: true,
      [Roles.IDM_THEKE]: false,
      [Roles.ADM_GVL]: false,
      [Roles.IDM_SPRINGER]: false,
      [Roles.IDM_SPRINGER_LEAD]: false,
      [Roles.IDM_PROCUREMENT]: false,
    },
    [Operations.UPDATE]: {
      [Roles.ADM]: false,
      [Roles.REGIONAL_LEAD]: false,
      [Roles.IDM_USER]: false,
      [Roles.IDM_DISPONENT]: false,
      [Roles.IDM_LEAD]: true,
      [Roles.IDM_THEKE]: false,
      [Roles.ADM_GVL]: false,
      [Roles.IDM_SPRINGER]: false,
      [Roles.IDM_SPRINGER_LEAD]: false,
      [Roles.IDM_PROCUREMENT]: false,
    },
  },
  [DomainObjects.REVENUES]: {
    [Operations.READ]: {
      [Roles.ADM]: true,
      [Roles.REGIONAL_LEAD]: true,
      [Roles.IDM_USER]: true,
      [Roles.IDM_DISPONENT]: true,
      [Roles.IDM_LEAD]: true,
      [Roles.IDM_THEKE]: true,
      [Roles.ADM_GVL]: true,
      [Roles.IDM_SPRINGER]: true,
      [Roles.IDM_SPRINGER_LEAD]: true,
      [Roles.IDM_PROCUREMENT]: true,
    },
  },
  [DomainObjects.SPECIAL_OFFERS]: {
    [Operations.READ]: {
      [Roles.ADM]: true,
      [Roles.REGIONAL_LEAD]: true,
      [Roles.IDM_USER]: true,
      [Roles.IDM_DISPONENT]: true,
      [Roles.IDM_LEAD]: true,
      [Roles.IDM_THEKE]: true,
      [Roles.ADM_GVL]: true,
      [Roles.IDM_SPRINGER]: true,
      [Roles.IDM_SPRINGER_LEAD]: true,
      [Roles.IDM_PROCUREMENT]: true,
    },
  },
  [DomainObjects.TOURS]: {
    [Operations.READ]: {
      [Roles.ADM]: true,
      [Roles.REGIONAL_LEAD]: true,
      [Roles.IDM_USER]: true,
      [Roles.IDM_DISPONENT]: true,
      [Roles.IDM_LEAD]: true,
      [Roles.IDM_THEKE]: true,
      [Roles.ADM_GVL]: true,
      [Roles.IDM_SPRINGER]: true,
      [Roles.IDM_SPRINGER_LEAD]: true,
      [Roles.IDM_PROCUREMENT]: true,
    },
    [Operations.UPDATE]: {
      [Roles.ADM]: true,
      [Roles.REGIONAL_LEAD]: true,
      [Roles.IDM_USER]: true,
      [Roles.IDM_DISPONENT]: true,
      [Roles.IDM_LEAD]: true,
      [Roles.IDM_THEKE]: true,
      [Roles.ADM_GVL]: true,
      [Roles.IDM_SPRINGER]: true,
      [Roles.IDM_SPRINGER_LEAD]: true,
      [Roles.IDM_PROCUREMENT]: true,
    },
    [Operations.DELETE]: {
      [Roles.ADM]: true,
      [Roles.REGIONAL_LEAD]: true,
      [Roles.IDM_USER]: true,
      [Roles.IDM_DISPONENT]: true,
      [Roles.IDM_LEAD]: true,
      [Roles.IDM_THEKE]: true,
      [Roles.ADM_GVL]: true,
      [Roles.IDM_SPRINGER]: true,
      [Roles.IDM_SPRINGER_LEAD]: true,
      [Roles.IDM_PROCUREMENT]: true,
    },
  },
  [DomainObjects.VENDORS]: {
    [Operations.READ]: {
      [Roles.ADM]: true,
      [Roles.REGIONAL_LEAD]: true,
      [Roles.IDM_USER]: true,
      [Roles.IDM_DISPONENT]: true,
      [Roles.IDM_LEAD]: true,
      [Roles.IDM_THEKE]: true,
      [Roles.ADM_GVL]: true,
      [Roles.IDM_SPRINGER]: true,
      [Roles.IDM_SPRINGER_LEAD]: true,
      [Roles.IDM_PROCUREMENT]: true,
    },
  },
  [DomainObjects.VISIT_REPORTS]: {
    [Operations.READ]: {
      [Roles.ADM]: true,
      [Roles.REGIONAL_LEAD]: true,
      [Roles.IDM_USER]: true,
      [Roles.IDM_DISPONENT]: true,
      [Roles.IDM_LEAD]: true,
      [Roles.IDM_THEKE]: true,
      [Roles.ADM_GVL]: true,
      [Roles.IDM_SPRINGER]: true,
      [Roles.IDM_SPRINGER_LEAD]: true,
      [Roles.IDM_PROCUREMENT]: true,
    },
    [Operations.UPDATE]: {
      [Roles.ADM]: true,
      [Roles.REGIONAL_LEAD]: true,
      [Roles.IDM_USER]: true,
      [Roles.IDM_DISPONENT]: true,
      [Roles.IDM_LEAD]: true,
      [Roles.IDM_THEKE]: true,
      [Roles.ADM_GVL]: true,
      [Roles.IDM_SPRINGER]: true,
      [Roles.IDM_SPRINGER_LEAD]: true,
      [Roles.IDM_PROCUREMENT]: true,
    },
    [Operations.DELETE]: {
      [Roles.ADM]: true,
      [Roles.REGIONAL_LEAD]: true,
      [Roles.IDM_USER]: true,
      [Roles.IDM_DISPONENT]: true,
      [Roles.IDM_LEAD]: true,
      [Roles.IDM_THEKE]: true,
      [Roles.ADM_GVL]: true,
      [Roles.IDM_SPRINGER]: true,
      [Roles.IDM_SPRINGER_LEAD]: true,
      [Roles.IDM_PROCUREMENT]: true,
    },
    [Operations.CREATE]: {
      [Roles.ADM]: true,
      [Roles.REGIONAL_LEAD]: true,
      [Roles.IDM_USER]: true,
      [Roles.IDM_DISPONENT]: true,
      [Roles.IDM_LEAD]: true,
      [Roles.IDM_THEKE]: true,
      [Roles.ADM_GVL]: true,
      [Roles.IDM_SPRINGER]: true,
      [Roles.IDM_SPRINGER_LEAD]: true,
      [Roles.IDM_PROCUREMENT]: true,
    },
  },
  [DomainObjects.VOLUME]: {
    [Operations.READ]: {
      [Roles.ADM]: true,
      [Roles.REGIONAL_LEAD]: true,
      [Roles.IDM_USER]: false,
      [Roles.IDM_DISPONENT]: false,
      [Roles.IDM_LEAD]: false,
      [Roles.IDM_THEKE]: false,
      [Roles.ADM_GVL]: true,
      [Roles.IDM_SPRINGER]: false,
      [Roles.IDM_SPRINGER_LEAD]: false,
      [Roles.IDM_PROCUREMENT]: false,
    },
  },
  [DomainObjects.COMPLAINTS]: {
    [Operations.READ]: {
      [Roles.ADM]: true,
      [Roles.REGIONAL_LEAD]: true,
      [Roles.IDM_USER]: true,
      [Roles.IDM_DISPONENT]: true,
      [Roles.IDM_LEAD]: true,
      [Roles.IDM_THEKE]: true,
      [Roles.ADM_GVL]: true,
      [Roles.IDM_SPRINGER]: true,
      [Roles.IDM_SPRINGER_LEAD]: true,
      [Roles.IDM_PROCUREMENT]: true,
    },
    [Operations.UPDATE]: {
      [Roles.ADM]: true,
      [Roles.REGIONAL_LEAD]: true,
      [Roles.IDM_USER]: true,
      [Roles.IDM_DISPONENT]: true,
      [Roles.IDM_LEAD]: true,
      [Roles.IDM_THEKE]: true,
      [Roles.ADM_GVL]: true,
      [Roles.IDM_SPRINGER]: true,
      [Roles.IDM_SPRINGER_LEAD]: true,
      [Roles.IDM_PROCUREMENT]: true,
    },
    [Operations.DELETE]: {
      [Roles.ADM]: true,
      [Roles.REGIONAL_LEAD]: true,
      [Roles.IDM_USER]: true,
      [Roles.IDM_DISPONENT]: true,
      [Roles.IDM_LEAD]: true,
      [Roles.IDM_THEKE]: true,
      [Roles.ADM_GVL]: true,
      [Roles.IDM_SPRINGER]: true,
      [Roles.IDM_SPRINGER_LEAD]: true,
      [Roles.IDM_PROCUREMENT]: true,
    },
    [Operations.CREATE]: {
      [Roles.ADM]: true,
      [Roles.REGIONAL_LEAD]: true,
      [Roles.IDM_USER]: true,
      [Roles.IDM_DISPONENT]: true,
      [Roles.IDM_LEAD]: true,
      [Roles.IDM_THEKE]: true,
      [Roles.ADM_GVL]: true,
      [Roles.IDM_SPRINGER]: true,
      [Roles.IDM_SPRINGER_LEAD]: true,
      [Roles.IDM_PROCUREMENT]: true,
    },
  },
  [DomainObjects.PICKLISTS]: {
    [Operations.READ]: {
      [Roles.ADM]: true,
      [Roles.REGIONAL_LEAD]: true,
      [Roles.IDM_USER]: true,
      [Roles.IDM_DISPONENT]: true,
      [Roles.IDM_LEAD]: true,
      [Roles.IDM_THEKE]: true,
      [Roles.ADM_GVL]: true,
      [Roles.IDM_SPRINGER]: true,
      [Roles.IDM_SPRINGER_LEAD]: true,
      [Roles.IDM_PROCUREMENT]: true,
    },
    [Operations.UPDATE]: {
      [Roles.ADM]: false,
      [Roles.REGIONAL_LEAD]: true,
      [Roles.IDM_USER]: true,
      [Roles.IDM_DISPONENT]: true,
      [Roles.IDM_LEAD]: true,
      [Roles.IDM_THEKE]: true,
      [Roles.ADM_GVL]: false,
      [Roles.IDM_SPRINGER]: true,
      [Roles.IDM_SPRINGER_LEAD]: true,
      [Roles.IDM_PROCUREMENT]: true,
    },
  },
  [DomainObjects.DOWNLOAD_CENTER]: {
    [Operations.READ]: {
      [Roles.ADM]: true,
      [Roles.REGIONAL_LEAD]: true,
      [Roles.IDM_USER]: true,
      [Roles.IDM_DISPONENT]: true,
      [Roles.IDM_LEAD]: true,
      [Roles.IDM_THEKE]: true,
      [Roles.ADM_GVL]: true,
      [Roles.IDM_SPRINGER]: true,
      [Roles.IDM_SPRINGER_LEAD]: true,
      [Roles.IDM_PROCUREMENT]: true,
    },
  },
}
